import { useId, useState } from 'react'
import { format } from 'date-fns'
import type { ExcludedSignal } from '../../types'
import { formatPnlPct } from '../../utils/pnl'
import { parseApiDate } from '../../utils/signalExpiry'
import TerminalPanel from '../dashboard/TerminalPanel'
import CallLink from '../signals/CallLink'
import { formatR } from './recordUI'
import { displaySymbol } from '../../utils/displaySymbol'

const when = (iso: string | null) => (iso ? format(parseApiDate(iso), 'MMM d, yyyy HH:mm') : '—')

/**
 * Closed signals excluded from the record: in none of the figures above, and
 * still here, each with the result it recorded -- unchanged, and muted so it
 * never reads as part of the record -- and the note saying why it no longer
 * counts. Nothing is deleted; wins and losses are excluded by the same rule.
 * The rows sit behind a labelled expander, collapsed by default and one click
 * away; the count on it is the rows the API returned, never a literal.
 */
const ExcludedSignalsPanel = ({ rows }: { rows: ExcludedSignal[] }) => {
  const [open, setOpen] = useState(false)
  const listId = `excluded-list-${useId().replace(/:/g, '')}`
  const noun = rows.length === 1 ? 'signal' : 'signals'
  return (
    <TerminalPanel
      title={`Excluded from the record (${rows.length})`}
      tone="amber"
      subtitle={
        <p className="text-xs text-gray-400" data-testid="excluded-explainer">
          Kept and listed, but not counted in any figure on this page. Each one shows the result it recorded, unchanged,
          and why it was excluded. Wins and losses are excluded by the same rule.
        </p>
      }
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={listId}
        className="rounded-md border border-amber-500/40 px-3 py-1.5 text-sm font-medium text-amber-300 hover:bg-amber-500/10"
        data-testid="excluded-toggle"
      >
        {open ? `Hide the ${rows.length} excluded ${noun} ↑` : `View ${rows.length} excluded ${noun} →`}
      </button>
      {open && (
        <ul id={listId} className="mt-3 divide-y divide-terminal-border" data-testid="excluded-list">
          {rows.map((row) => (
            <li key={row.signal_id} className="py-3 text-sm" data-testid="excluded-row">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <CallLink id={row.signal_id} />
                <span className="font-semibold text-gray-200">{displaySymbol(row.symbol)}</span>
                <span className="text-xs uppercase text-gray-400">
                  {row.signal_type} · {row.timeframe ?? '—'}
                </span>
                <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300 ring-1 ring-amber-500/30">
                  Excluded
                </span>
                <span className="text-xs text-gray-500">closed {when(row.closed_at)}</span>
                <span className="ml-auto font-mono text-xs text-gray-500" data-testid="excluded-recorded">
                  recorded {row.outcome.toUpperCase()}
                  {row.profit_loss_percent != null && ` · ${formatPnlPct(row.profit_loss_percent)}`}
                  {row.r_multiple != null && ` · ${formatR(row.r_multiple)} gross`}
                </span>
              </div>
              {row.exclusion_note && (
                <p className="mt-1 text-xs text-gray-400" data-testid="excluded-note">
                  {row.exclusion_note}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </TerminalPanel>
  )
}

export default ExcludedSignalsPanel
